// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { AbstractHTMLMesh } from '@/_models/virtualStaging/HTMLMesh/component/AbstractHTMLMesh';
import { ControllerType, Inputs } from '@/_models/virtualStaging/HTMLMesh/component/Menu/types';
import { createHTMLMesh } from '@/_models/virtualStaging/HTMLMesh/HTMLMesh';
import { InteractiveGroup } from '@/_models/virtualStaging/HTMLMesh/utils/InteractiveGroup';
import { ComponentInteractionType } from '@/_models/virtualStaging/Scene.component';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { getXZCoordinatesOnCircle } from '@/components/util/math/getXZCoordinatesOnCircle';
import { Mesh, MeshStandardMaterial, Vector3 } from 'three';

export class MenuMeshComponent extends AbstractHTMLMesh {
  private hostPose;

  public selectedItemIndex = 0;

  public mesh;

  public interactiveGroup;

  public gamepadButtonCallback = null;

  public thumbStickCallback = null;

  public triggerCallback = null;

  // Track per-controller input state to prevent cross-controller toggle thrashing
  public gamepadButtonActiveByController: Record<string, boolean> = {
    [ControllerType.GamepadL]: false,
    [ControllerType.GamepadR]: false,
  };
  public thumbStickYActiveByController: Record<string, boolean> = {
    [ControllerType.GamepadL]: false,
    [ControllerType.GamepadR]: false,
  };
  public triggerActiveByController: Record<string, boolean> = {
    [ControllerType.GamepadL]: false,
    [ControllerType.GamepadR]: false,
  };

  inputs: Inputs = {
    html: null,
    visible: false,
    scale: 1,
    itemRefArr: [],
    controllers: [ControllerType.GamepadL],
    controllerButtonIndex: 4,
    yOffset: -0.2,
  };

  onInit() {
    const { html } = this.inputs;
    this.initMesh(html);
    this.initRoot();
    this.addMeshPointerEvents();
    this.getXRController();
    this.outputs.loadingState = LoadingState.Loaded;
  }

  initMesh(html) {
    this.mesh = createHTMLMesh(html, this.context.three);
    this.mesh.material.side = this.context.three.DoubleSide;
    this.mesh.material.depthWrite = false;
    this.mesh.material.depthTest = false;
    this.mesh.renderOrder = 980;
    this.mesh.scale.setScalar(2);
    this.mesh.userData = { isPointerOver: false };
  }

  addMeshPointerEvents() {
    this.interactiveGroup = new InteractiveGroup(
      this.context.renderer,
      this.context.camera,
      this.context,
    );
    this.pivot.add(this.interactiveGroup);
    this.interactiveGroup.add(this.mesh);

    // Add click event to close menu when item is selected
    this.mesh.addEventListener('click', () => {
      this.onMenuItemClick();
    });
  }

  setHostPose(pose) {
    this.hostPose = pose;
  }

  setPositionFromPose(pose) {
    if (!pose.rotation || !pose.position) {
      console.error('No pose', pose);
      console.trace(pose);
      return;
    }
    const { x, z } = getXZCoordinatesOnCircle(0.85, pose.rotation.y + 180);
    this.root.position.set(
      pose.position.x + x,
      pose.position.y + this.inputs.yOffset,
      pose.position.z + z,
    );
    this.root.lookAt(pose.position.x, pose.position.y - 0.1, pose.position.z);
  }

  setFromHostPose() {
    this.setPositionFromPose(this.hostPose);
  }

  setFromController(controller: any) {
    const controllerWorld = new Vector3();
    controller.getWorldPosition(controllerWorld);
    this.root.position.copy(controllerWorld);
  }

  updateMesh() {
    this.interactiveGroup.traverse((el: Mesh) => {
      if (el.material) {
        const material = el.material as MeshStandardMaterial;
        if (material.map) material.map.dispose();
        material.dispose();
      }
      if (el.geometry) {
        el.geometry.dispose();
      }
    });
    this.pivot.remove(this.interactiveGroup);
    this.initMesh(this.inputs.html);
    this.addMeshPointerEvents();
  }

  selectItem(index: number) {
    if (!this.inputs.itemRefArr[this.selectedItemIndex] || !this.inputs.itemRefArr[index]) return;
    this.inputs.itemRefArr[this.selectedItemIndex].style.backgroundColor = 'transparent';
    this.inputs.itemRefArr[index].style.backgroundColor = 'blue';
    this.selectedItemIndex = index;
  }

  selectItemUp() {
    this.selectItem(
      this.inputs.itemRefArr.length - 1 === this.selectedItemIndex ? 0 : this.selectedItemIndex + 1,
    );
  }

  selectItemDown() {
    this.selectItem(
      this.selectedItemIndex === 0 ? this.inputs.itemRefArr.length - 1 : this.selectedItemIndex - 1,
    );
  }

  // Override parent setVisible method to properly handle interactive elements
  setVisible(visible: boolean) {
    this.inputs.visible = visible;
    if (this.onVisibleChange) this.onVisibleChange();

    if (!visible && this.onEvent) {
      this.onEvent(ComponentInteractionType.HOVER, { hover: false });
    }

    // Disable/enable interactive group to prevent ghost interactions
    if (this.interactiveGroup) {
      this.interactiveGroup.visible = visible;

      // Disable raycast entirely when hidden to prevent trigger interactions
      this.interactiveGroup.traverse((child) => {
        if (child.isMesh) {
          if (visible) {
            // Restore original raycast method if it was stored
            if (child.userData.originalRaycast) {
              child.raycast = child.userData.originalRaycast;
            }
          } else {
            // Store original raycast method and replace with empty function
            if (!child.userData.originalRaycast) {
              child.userData.originalRaycast = child.raycast;
            }
            child.raycast = () => {};
          }
        }
      });
    }
  }

  onMenuItemClick() {
    // Close menu when any item is clicked
    this.setVisible(false);
    // Manually trigger hover-out event to hide cursor
    if (this.onEvent) {
      this.onEvent(ComponentInteractionType.HOVER, { hover: false });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  cycleVR(_session) {
    if (this.hostPose && this.inputs.visible) this.setFromHostPose();

    this.inputs.controllers.forEach((controller) => {
      // Thumbstick Y per-controller debounce
      const thumbActive = this.thumbStickYActiveByController[controller];
      if (this[controller]) {
        const axisY = this[controller].axes[3];
        if (!thumbActive && (axisY < -0.5 || axisY > 0.5)) {
          this.thumbStickYActiveByController[controller] = true;
          const directionY = axisY > 0.5 ? 1 : -1;
          if (directionY === 1) this.selectItemUp();
          if (directionY === -1) this.selectItemDown();
          if (this.thumbStickCallback) this.thumbStickCallback(this[controller].axes);
        }
        if (thumbActive && axisY >= -0.5 && axisY <= 0.5) {
          this.thumbStickYActiveByController[controller] = false;
        }
      }

      // Side button toggle per-controller debounce
      const btnActive = this.gamepadButtonActiveByController[controller];
      const btnPressed = this[controller]?.buttons[this.inputs.controllerButtonIndex]?.pressed;
      if (btnPressed && !btnActive && this.hostPose) {
        this.setVisible(!this.inputs.visible);
        this.gamepadButtonActiveByController[controller] = true;
        if (this.gamepadButtonCallback)
          this.gamepadButtonCallback(this[controller]?.buttons[this.inputs.controllerButtonIndex]);
      }
      if (!btnPressed && btnActive) {
        this.gamepadButtonActiveByController[controller] = false;
      }

      // Confirm/select with primary trigger (button index 0) per-controller debounce
      const trigActive = this.triggerActiveByController[controller];
      const trigPressed = this[controller]?.buttons[0]?.pressed;
      if (trigPressed && !trigActive && this.hostPose) {
        this.triggerActiveByController[controller] = true;
        if (this.triggerCallback) this.triggerCallback(this[controller]?.buttons);
      }
      if (!trigPressed && trigActive) {
        this.triggerActiveByController[controller] = false;
      }
    });
  }

  onTick() {
    if (this.context?.renderer?.xr?.isPresenting) {
      const _session = this.context.renderer.xr.getSession();
      _session.requestAnimationFrame(() => this.cycleVR(_session));
    }
  }
}

export const menuMeshComponentType = 'mp.menuMeshComponent';
export const makeMenuMeshComponent = (): any => new MenuMeshComponent();
